import { trackGuess } from "./analytics";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Airport } from "@/models/airport";
import {
  evaluateGuess,
  hasWon,
  isFinished,
  loadGuessedIatas,
  progressKey,
  saveGuessedIatas,
} from "./game";
import { DailyClue, DailyGuess, DailyPuzzle } from "./types";

// What plays after a guess, one step at a time:
//   checking -> the map pulls back and a dotted grey line slowly reaches from the
//               plane toward the guess; the verdict is unknown
//   verdict  -> the pin lands; a correct guess turns the line solid green
//   landing  -> correct guesses only: the plane flies in and lands
//   distance -> wrong guesses: the "Not Rome / 650 miles away" pill
//   clue     -> the new clue is revealed as a pill with a gold glow
//   settle   -> the clue has joined the others, top-left, and still glows
// The last guess, right or wrong, ends on the end screen.
export type FeedbackPhase =
  | "checking"
  | "verdict"
  | "landing"
  | "distance"
  | "clue"
  | "settle";

const PHASE_MS: Record<FeedbackPhase, number> = {
  checking: 2100,
  verdict: 1100,
  landing: 1350,
  distance: 2400,
  clue: 6000,
  settle: 2000,
};

interface Feedback {
  guessIndex: number;
  phase: FeedbackPhase;
}

export interface RevealedClue {
  clue: DailyClue;
  number: number;
  isNew: boolean;
}

export const useDailyGame = (puzzle: DailyPuzzle, airports: Airport[]) => {
  const [guesses, setGuesses] = useState<DailyGuess[]>(() =>
    loadGuessedIatas(progressKey(puzzle))
      .map((iata) => airports.find((airport) => airport.iata === iata))
      .filter((airport): airport is Airport => !!airport)
      .map((airport) => evaluateGuess(puzzle, airport))
  );
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  const phase = feedback?.phase ?? null;
  const feedbackGuess = feedback ? guesses[feedback.guessIndex] : null;
  // While a guess is being checked, nothing may give away its result.
  const isChecking = phase === "checking";
  const busy = phase === "checking" || phase === "verdict" || phase === "landing";

  const submitGuess = useCallback(
    (airport: Airport) => {
      if (busy || isFinished(guesses)) return;
      if (guesses.some((guess) => guess.airport.iata === airport.iata)) return;

      const next = [...guesses, evaluateGuess(puzzle, airport)];
      setGuesses(next);
      saveGuessedIatas(
        progressKey(puzzle),
        next.map((guess) => guess.airport.iata)
      );
      setFeedback({ guessIndex: next.length - 1, phase: "checking" });
      trackGuess(puzzle, next, isFinished(next));
    },
    [busy, guesses, puzzle]
  );

  const advanceFeedback = useCallback(() => {
    setFeedback((current) => {
      if (!current) return null;
      const correct = guesses[current.guessIndex]?.band === "correct";
      switch (current.phase) {
        case "checking":
          return { ...current, phase: "verdict" };
        case "verdict":
          return { ...current, phase: correct ? "landing" : "distance" };
        case "distance":
          // A fifth wrong guess goes to the end screen, not to a clue.
          return isFinished(guesses) ? null : { ...current, phase: "clue" };
        case "clue":
          return { ...current, phase: "settle" };
        default:
          return null;
      }
    });
  }, [guesses]);

  const clearFeedback = useCallback(() => setFeedback(null), []);

  useEffect(() => {
    if (!feedback) return;
    const timer = setTimeout(advanceFeedback, PHASE_MS[feedback.phase]);
    return () => clearTimeout(timer);
  }, [feedback, advanceFeedback]);

  // One clue per wrong guess, up to the number of clues. The newest stays
  // hidden until its distance pill has finished.
  const wrongCount = guesses.filter((guess) => guess.band !== "correct").length;
  const earnedCount = Math.min(wrongCount, puzzle.clues.length);
  const newClueIndex =
    feedbackGuess && feedbackGuess.band !== "correct" && wrongCount <= puzzle.clues.length
      ? wrongCount - 1
      : -1;
  const newClueHidden =
    newClueIndex >= 0 &&
    (phase === "checking" || phase === "verdict" || phase === "distance");
  const revealedCount = newClueHidden ? earnedCount - 1 : earnedCount;

  const revealedClues: RevealedClue[] = useMemo(
    () =>
      puzzle.clues.slice(0, Math.max(0, revealedCount)).map((clue, index) => ({
        clue,
        number: index + 1,
        isNew: phase === "settle" && index === newClueIndex,
      })),
    [puzzle, revealedCount, newClueIndex, phase]
  );

  return {
    // Guesses whose result has been shown. The one being checked is left out.
    guesses: isChecking ? guesses.slice(0, -1) : guesses,
    allGuesses: guesses,
    won: hasWon(guesses),
    // The end screen waits for the last guess's sequence to finish.
    finished: isFinished(guesses) && !feedback,
    busy,
    submitGuess,
    feedbackPhase: phase,
    feedbackGuess,
    advanceFeedback,
    clearFeedback,
    revealedClues,
  };
};
